import React, { useState, useEffect, useRef } from "react";
import "./Board.css";
import HowToPlayModal from "./HowToPlayModal";
import { buildShareText, guessLine, summaryLine } from "../utils/shareText";
import Toast from "./Toast";
import Countdown from "./Countdown";
import { iconLabel } from "../utils/iconLabel";
import { GameChecker, CheckResult } from "../utils/gameChecker";
import { moveTile, removeTile, findTile, findRowIndexAt } from "../utils/boardMoves";

// Copy text, with a fallback for browsers without the async clipboard API
const copyText = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
};

interface Tile {
  icon: string;
}


interface BoardProps {
  tiles: Tile[];
  onRowComplete: (rowIndex: number[], wasIncorrect?: boolean) => void;
  completedRows: boolean[];
  guessesLeft: number;
  incorrectGuesses: number;
  checker: GameChecker;
  isPreview?: boolean; // admin "Test puzzle": no countdown or sharing
  onFinish?: (result: { won: boolean; tries: number; seconds: number }) => void;
  startDate?: string;
  nextStartDate?: string | null;
}

const Board: React.FC<BoardProps> = ({
  tiles,
  onRowComplete,
  completedRows,
  guessesLeft,
  incorrectGuesses,
  checker,
  isPreview,
  onFinish,
  startDate,
  nextStartDate,
}) => {
  // Group info is only known once the server confirms a row (or reveals on loss)
  const [iconGroups, setIconGroups] = useState<Record<string, number>>({});
  const [groupNameMap, setGroupNameMap] = useState<Record<number, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const clearToast = React.useCallback(() => setToast(null), []);

  const fetchReveal = () => checker.reveal();

  // All tiles start in the pool
  const [rows, setRows] = useState<(Tile | undefined)[][]>([
    [undefined, undefined, undefined, undefined],
    [undefined, undefined, undefined, undefined],
    [undefined, undefined, undefined, undefined],
    [undefined, undefined, undefined, undefined],
  ]);
  // Tiles not yet placed in any row
  // Order of the unplaced tiles (changed by the Shuffle button)
  const [poolOrder, setPoolOrder] = useState<string[]>(() => tiles.map((t) => t.icon));
  useEffect(() => setPoolOrder(tiles.map((t) => t.icon)), [tiles]);
  const placed = new Set(rows.flat().filter(Boolean).map((t) => (t as Tile).icon));
  const tileByIcon = new Map(tiles.map((t) => [t.icon, t]));
  const poolTiles = poolOrder
    .filter((icon) => !placed.has(icon))
    .map((icon) => tileByIcon.get(icon))
    .filter((t): t is Tile => !!t);

  const handleShuffle = () => {
    setPoolOrder((prev) => {
      const next = [...prev];
      for (let i = next.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [next[i], next[j]] = [next[j], next[i]];
      }
      return next;
    });
  };
  const [draggedTile, setDraggedTile] = useState<Tile | null>(null);
  const [dragOver, setDragOver] = useState<{
    row: number;
    index: number;
  } | null>(null);
  const [touchTile, setTouchTile] = useState<Tile | null>(null);
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(
    null
  );
  const [dragPreview, setDragPreview] = useState<{
    tile: Tile;
    x: number;
    y: number;
  } | null>(null);
  // Desktop drag preview state (similar to mobile)
  const [desktopDragPreview, setDesktopDragPreview] = useState<{
    tile: Tile;
    x: number;
    y: number;
  } | null>(null);
  
  // Refs for managing non-passive touch events
  const tilesContainerRef = useRef<HTMLDivElement>(null);
  const rowsContainerRef = useRef<HTMLDivElement>(null);

  // Timer state
  const [seconds, setSeconds] = useState(0);
  const [timerActive, setTimerActive] = useState(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Track which rows were guessed incorrectly on last submit
  const [lastIncorrectRows, setLastIncorrectRows] = useState<boolean[]>([
    false,
    false,
    false,
    false,
  ]);

  // Track previous guesses (as stringified rows)
  const [previousGuesses, setPreviousGuesses] = useState<string[]>([]);

  // Start/stop timer based on guessesLeft and completedRows
  useEffect(() => {
    if (timerActive && guessesLeft > 0 && !completedRows.every(Boolean)) {
      intervalRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [timerActive]);
  useEffect(() => {
    if (completedRows.every(Boolean) || guessesLeft <= 0) {
      setTimerActive(false);
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
  }, [completedRows, guessesLeft]);

  // Format timer as mm:ss
  const formatTime = (s: number) =>
    `${Math.floor(s / 60)
      .toString()
      .padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`;
  // "Week of October 26, 2026": the puzzle's week, not today's date (dates are stored as UTC days)
  const dateStr = startDate
    ? `Week of ${new Date(startDate).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" })}`
    : "";

  // How to play popover state
  const [showHowTo, setShowHowTo] = useState(false);
  const [showResults, setShowResults] = useState(false);
  
  // Share popover state
  // One entry per submission: which rows were correct after it (for the share grid)
  const [guessLog, setGuessLog] = useState<boolean[][]>([]);
  const [copied, setCopied] = useState(false);

  // Drag start from pool
  const handleTileDragStart = (
    event: React.DragEvent<HTMLDivElement>,
    tile: Tile
  ) => {
    if (gameLost) return;
    setSelected(null);
    setDraggedTile(tile);
    event.dataTransfer.setData("text/plain", tile.icon);
    
    // Hide the default drag image
    const dragImage = new Image();
    dragImage.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=';
    event.dataTransfer.setDragImage(dragImage, 0, 0);
    
    // Add visual feedback similar to mobile
    const target = event.currentTarget;
    target.style.opacity = '0.5';
    target.style.transform = 'scale(0.95)';
    
    // Create desktop drag preview
    setDesktopDragPreview({
      tile: tile,
      x: event.clientX,
      y: event.clientY
    });
  };

  // Drop on empty or filled spot in row (for rearranging or swapping)
  const handleTileDropOnRow = (rowIndex: number, tileIndex: number) => {
    if (gameLost || !draggedTile || completedRows[rowIndex]) return;
    setRows(moveTile(rows, draggedTile, rowIndex, tileIndex));
    handleDesktopDragEnd();
  };

  // Allow dropping back to pool (remove from row)
  const handleDropToPool = () => {
    if (gameLost || !draggedTile) return;
    setRows(removeTile(rows, draggedTile));
    handleDesktopDragEnd();
  };

  // ---- Tap to place (works alongside drag and drop) ----
  // Tap a tile to select it, then tap a spot, a row, another tile (swap)
  // or the tile area (send back). Tap the selected tile again to cancel.
  const [selected, setSelected] = useState<Tile | null>(null);
  const canAct = guessesLeft > 0 && !completedRows.every(Boolean);
  const isSelected = (t?: Tile) => !!t && !!selected && selected.icon === t.icon;

  const handleTileTap = (tile: Tile, where: { row: number; idx: number } | "pool") => {
    if (!canAct) return;
    if (where !== "pool" && completedRows[where.row]) return;
    if (!selected) return setSelected(tile);
    if (selected.icon === tile.icon) return setSelected(null);
    const selectedSpot = findTile(rows, selected);
    if (where === "pool") {
      if (!selectedSpot) return setSelected(tile); // pick a different unplaced tile
      // A placed tile is selected and an unplaced one tapped: swap them
      setRows(moveTile(rows, tile, selectedSpot[0], selectedSpot[1]));
    } else {
      setRows(moveTile(rows, selected, where.row, where.idx)); // swap or replace
    }
    setSelected(null);
  };

  const handleRowTap = (row: number) => {
    if (!selected || !canAct || completedRows[row]) return;
    const spot = findTile(rows, selected);
    if (spot && spot[0] === row) return setSelected(null);
    if (rows[row].every(Boolean)) {
      setToast("That row is full. Tap one of its tiles to swap.");
      return;
    }
    setRows(moveTile(rows, selected, row, -1));
    setSelected(null);
  };

  const handlePoolTap = () => {
    if (!selected || !canAct) return;
    if (findTile(rows, selected)) setRows(removeTile(rows, selected));
    setSelected(null);
  };

  const tapKeys = (fn: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      fn();
    } else if (e.key === "Escape") {
      setSelected(null);
    }
  };

  // Submit: ask the server which full rows are correct
  const handleSubmit = async () => {
    if (submitting) return;
    setSelected(null);
    // Serialize current guess
    const guessString = rows
      .map((row) => row.map((t) => t?.icon || "").join(","))
      .join("|");
    if (previousGuesses.includes(guessString)) {
      setToast("You've already tried this arrangement!");
      return;
    }

    const rowIndexes = rows
      .map((row, i) => (!completedRows[i] && row.every((t) => t) ? i : -1))
      .filter((i) => i >= 0);
    if (rowIndexes.length === 0) return;

    setSubmitting(true);
    let results: CheckResult[];
    try {
      results = await checker.check(rowIndexes.map((r) => rows[r].map((t) => (t as Tile).icon)));
    } catch (err) {
      console.error("Error checking guess:", err);
      setToast("We couldn't check your answer. Please try again.");
      return;
    } finally {
      setSubmitting(false);
    }
    setPreviousGuesses((prev) => [...prev, guessString]);

    const rowsToLock: number[] = [];
    const incorrectRows: boolean[] = [false, false, false, false];
    let hasIncorrectGuess = false;
    const newIconGroups: Record<string, number> = {};
    const newGroupNames: Record<number, string> = {};

    results.forEach((result, k) => {
      const rowIndex = rowIndexes[k];
      if (result.correct && result.groupIndex !== undefined) {
        rowsToLock.push(rowIndex);
        rows[rowIndex].forEach((t) => {
          if (t) newIconGroups[t.icon] = result.groupIndex as number;
        });
        newGroupNames[result.groupIndex] = result.groupName || "";
      } else {
        incorrectRows[rowIndex] = true;
        hasIncorrectGuess = true;
      }
    });
    setIconGroups((prev) => ({ ...prev, ...newIconGroups }));
    setGroupNameMap((prev) => ({ ...prev, ...newGroupNames }));
    setLastIncorrectRows(incorrectRows);
    setGuessLog((prev) => [...prev, [0, 1, 2, 3].map((r) => completedRows[r] || rowsToLock.includes(r))]);
    // If any row was incorrect, the whole submit counts as one incorrect guess
    onRowComplete(rowsToLock, hasIncorrectGuess);
  };


  // Clear incorrect row highlight when tiles are moved
  useEffect(() => {
    // Only clear incorrect highlight if the row contents actually change
    setLastIncorrectRows((prev) =>
      prev.map((wasIncorrect, i) => {
        if (!wasIncorrect) return false;
        // If the row contents have changed, clear the incorrect highlight
        const rowTiles = rows[i].map((t) => t?.icon || null).join(",");
        const prevRowTiles = prevRows.current[i];
        if (rowTiles !== prevRowTiles) return false;
        return true;
      })
    );
    // Update prevRows ref
    prevRows.current = rows.map((row) =>
      row.map((t) => t?.icon || null).join(",")
    );
  }, [rows, completedRows]);

  // Track previous row contents for comparison
  const prevRows = useRef<string[]>(
    rows.map((row) => row.map((t) => t?.icon || null).join(","))
  );

  // Global touch event handlers for smooth drag preview on iOS
  useEffect(() => {
    const handleGlobalTouchMove = (e: TouchEvent) => {
      if (touchTile) {
        e.preventDefault(); // Prevent scrolling - works with { passive: false }
        const touch = e.touches[0];
        
        // Update drag preview position with requestAnimationFrame for smooth animation
        // Only update if we currently have a touchTile and preview should be active
        requestAnimationFrame(() => {
          // Double-check touchTile still exists before updating preview
          if (touchTile) {
            setDragPreview({
              tile: touchTile,
              x: touch.clientX,
              y: touch.clientY
            });
          }
        });
        
        // Highlight the row under the finger
        const overRow = findRowIndexAt(touch.clientX, touch.clientY, completedRows);
        setDragOver(overRow === null ? null : { row: overRow, index: 0 });
      }
    };

    const handleGlobalTouchEnd = (e: TouchEvent) => {
      // Always clear drag preview and touch state when touch ends anywhere
      // Don't check for touchTile as it might be stale
      setDragPreview(null);
      setTouchTile(null);
      setDragOver(null);
      
      // Reset any visual feedback on all tiles
      const tiles = document.querySelectorAll('.tile');
      tiles.forEach(tile => {
        const element = tile as HTMLElement;
        element.style.opacity = '';
        element.style.transform = '';
      });
      
      // Add a small delay to catch any race conditions with quick swipes
      setTimeout(() => {
        setDragPreview(null);
        setTouchTile(null);
        setDragOver(null);
      }, 10);
    };

    // Always listen for touchend to ensure cleanup
    document.addEventListener('touchend', handleGlobalTouchEnd, { passive: false });
    
    if (touchTile) {
      document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
    }
    
    return () => {
      document.removeEventListener('touchend', handleGlobalTouchEnd);
      if (touchTile) {
        document.removeEventListener('touchmove', handleGlobalTouchMove);
      }
    };
  }, [touchTile, completedRows]);

  // Touch handlers for mobile drag
  const handleTileTouchStart = (
    tile: Tile,
    e: React.TouchEvent<HTMLDivElement>
  ) => {
    if (gameLost) return;
    
    // Clear any existing drag preview first (safety cleanup)
    setDragPreview(null);
    
    // Note: preventDefault removed to avoid passive event listener error
    // Touch behavior prevention is handled by the global touch move listener
    setTouchTile(tile);
    const touch = e.touches[0];
    setTouchStart({ x: touch.clientX, y: touch.clientY });
    
    // Add visual feedback
    const target = e.currentTarget;
    target.style.opacity = '0.5';
    target.style.transform = 'scale(0.95)';
    
    // Delay drag preview creation slightly to avoid race conditions with quick swipes
    setTimeout(() => {
      // Only create drag preview if touchTile still exists (not cleaned up by quick touchend)
      setTouchTile(currentTile => {
        if (currentTile && currentTile.icon === tile.icon) {
          setDragPreview({
            tile: tile,
            x: touch.clientX,
            y: touch.clientY
          });
          return currentTile;
        }
        return currentTile;
      });
    }, 50);
  };
  
  const handleTileTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (gameLost) return;
    if (touchTile) {
      // Note: preventDefault is handled in the global listener
      e.stopPropagation(); // Stop event bubbling
      
      const touch = e.touches[0];
      
      // Force update drag preview position immediately
      requestAnimationFrame(() => {
        setDragPreview({
          tile: touchTile,
          x: touch.clientX,
          y: touch.clientY
        });
      });
      
      // Highlight the row under the finger
      const overRow = findRowIndexAt(touch.clientX, touch.clientY, completedRows);
      setDragOver(overRow === null ? null : { row: overRow, index: 0 });
    }
  };
  
  const handleTileTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (gameLost || !touchTile) return;
    
    // Get element at touch end position and capture state before clearing
    const touch = e.changedTouches[0];
    const currentTouchTile = touchTile; // Capture before state is cleared
    const currentDragOver = dragOver; // Capture before state is cleared
    
    // Immediately clear drag preview and touch state - force cleanup
    setDragPreview(null);
    setTouchTile(null);
    setDragOver(null);
    
    // A tap (finger barely moved) is handled by tap-to-place via onClick
    if (touchStart && Math.hypot(touch.clientX - touchStart.x, touch.clientY - touchStart.y) < 10) {
      const t = e.currentTarget;
      t.style.opacity = '';
      t.style.transform = '';
      setTouchStart(null);
      return;
    }
    setSelected(null);

    // Reset visual feedback
    const target = e.currentTarget;
    target.style.opacity = '';
    target.style.transform = '';
    
    const handleTouchDropOnRow = (rowIndex: number, tileIndex: number, tileToMove: Tile) => {
      if (gameLost || completedRows[rowIndex]) return;
      setRows(moveTile(rows, tileToMove, rowIndex, tileIndex));
    };
    const handleTouchDropToPool = (tileToMove: Tile) => {
      if (gameLost) return;
      setRows(removeTile(rows, tileToMove));
    };

    // Try multiple approaches to find the drop target
    let dropSuccessful = false;
    
    // Method 1: Use elementFromPoint
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    
    if (element) {
      // Check if dropped on a specific tile
      const tileEl = element.closest('.tile');
      if (tileEl && tileEl.closest('.row') && !tileEl.closest('.row')?.classList.contains('locked')) {
        const rowEl = tileEl.closest('.row');
        const rowsContainer = rowEl?.parentElement;
        if (rowsContainer) {
          const rowIndex = Array.from(rowsContainer.children).indexOf(rowEl);
          // Find which tile in the row was targeted
          const tilesInRow = rowEl?.querySelectorAll('.tile');
          const tileIndex = tilesInRow ? Array.from(tilesInRow).indexOf(tileEl) : 0;
          
          if (rowIndex >= 0 && rowIndex < 4) {
            handleTouchDropOnRow(rowIndex, tileIndex, currentTouchTile);
            dropSuccessful = true;
          }
        }
      }
      // Check if dropped on a row (but not on a specific tile)
      else if (element.closest('.row') && !element.closest('.row')?.classList.contains('locked')) {
        const rowEl = element.closest('.row');
        const rowsContainer = rowEl?.parentElement;
        if (rowsContainer) {
          const rowIndex = Array.from(rowsContainer.children).indexOf(rowEl);
          if (rowIndex >= 0 && rowIndex < 4) {
            handleTouchDropOnRow(rowIndex, -1, currentTouchTile); // -1 means append to end
            dropSuccessful = true;
          }
        }
      } 
      // Check if dropped on pool
      else if (element.closest('.tiles-container')) {
        handleTouchDropToPool(currentTouchTile);
        dropSuccessful = true;
      }
    }
    
    // Method 2: If first method failed, check using drag over state
    if (!dropSuccessful && currentDragOver) {
      const rowIndex = currentDragOver.row;
      if (rowIndex >= 0 && rowIndex < 4 && !completedRows[rowIndex]) {
        handleTouchDropOnRow(rowIndex, -1, currentTouchTile); // -1 means append to end
        dropSuccessful = true;
      }
    }
    
    // Method 3: If still no success, check row positions with a wider margin
    if (!dropSuccessful) {
      const rowIndex = findRowIndexAt(touch.clientX, touch.clientY, completedRows, 10);
      if (rowIndex !== null) {
        handleTouchDropOnRow(rowIndex, -1, currentTouchTile); // -1 means append to end
        dropSuccessful = true;
      }
    }
    
    // Final cleanup - setTouchStart
    setTouchStart(null);
    // Note: Other state already cleared at the beginning of this function
  };

  // Only show in local dev
  const isLocal =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";

  // Auto-solve handler (only in local dev)
  const handleAutoSolve = async () => {
    try {
      const { groups } = await fetchReveal();
      const tileByIcon: Record<string, Tile> = {};
      tiles.forEach((t) => (tileByIcon[t.icon] = t));
      // Place correct tiles in each row. Do NOT submit or lock in rows.
      setRows(groups.map((g) => g.map((icon) => tileByIcon[icon])));
    } catch (err) {
      console.error("Auto-solve failed:", err);
    }
  };

  const gameLost = guessesLeft <= 0;

  // Report the result once, when the game ends (used for stats)
  const finishedRef = useRef(false);
  useEffect(() => {
    const won = completedRows.every(Boolean);
    if (finishedRef.current || (!won && !gameLost)) return;
    finishedRef.current = true;
    onFinish?.({ won, tries: guessLog.length, seconds });
  }, [completedRows, gameLost]);

  const shareText = () =>
    buildShareText({
      startDate,
      guesses: guessLog,
      won: completedRows.every(Boolean),
      seconds,
    });

  // Phones: open the native share sheet (Messages, WhatsApp...). Desktop: copy.
  const handleShare = async () => {
    const text = shareText();
    const isTouch = window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
    if (isTouch && navigator.share) {
      try {
        await navigator.share({ text });
        return;
      } catch (err: any) {
        if (err && err.name === "AbortError") return; // they closed the share sheet
      }
    }
    const done = await copyText(text);
    setCopied(done);
    setToast(done ? "Results copied. Paste them anywhere!" : "Couldn't copy. Try again.");
    if (done) setTimeout(() => setCopied(false), 2000);
  };

  // When the game is lost, fetch the answers and show every group.
  // Runs once when the game becomes lost; uses a functional update so it
  // doesn't depend on `rows` (depending on it caused an endless update loop).
  useEffect(() => {
    if (!gameLost) return;
    let cancelled = false;
    fetchReveal()
      .then(({ groups, groupNames }) => {
        if (cancelled) return;
        const tileByIcon: Record<string, Tile> = {};
        tiles.forEach((t) => (tileByIcon[t.icon] = t));
        const fullIconGroups: Record<string, number> = {};
        groups.forEach((g, gi) => g.forEach((icon) => (fullIconGroups[icon] = gi)));
        setIconGroups(fullIconGroups);
        setGroupNameMap(Object.fromEntries(groupNames.map((n, i) => [i, n])));

        setRows((prevRows) => {
          const newRows: (Tile | undefined)[][] = Array(4).fill(null);
          const usedGroups = new Set<number>();
          // Keep correctly guessed rows in place
          for (let rowIndex = 0; rowIndex < 4; rowIndex++) {
            const first = prevRows[rowIndex][0];
            const group = first ? fullIconGroups[first.icon] : undefined;
            if (completedRows[rowIndex] && group !== undefined) {
              newRows[rowIndex] = groups[group].map((icon) => tileByIcon[icon]);
              usedGroups.add(group);
            }
          }
          // Fill the remaining rows with the unsolved groups
          const remainingGroups = [0, 1, 2, 3].filter((g) => !usedGroups.has(g));
          for (let rowIndex = 0; rowIndex < 4; rowIndex++) {
            if (!newRows[rowIndex]) {
              const g = remainingGroups.shift() as number;
              newRows[rowIndex] = (groups[g] || []).map((icon) => tileByIcon[icon]);
            }
          }
          return newRows;
        });
      })
      .catch((err) => console.error("Error revealing answers:", err));
    return () => {
      cancelled = true;
    };
  }, [gameLost]);

  // Desktop drag handlers
  const handleDesktopDragEnd = () => {
    // Clear drag preview and reset visual state
    setDesktopDragPreview(null);
    setDraggedTile(null);
    setDragOver(null);
    
    // Reset visual feedback on all tiles
    const tiles = document.querySelectorAll('.tile');
    tiles.forEach(tile => {
      const element = tile as HTMLElement;
      element.style.opacity = '';
      element.style.transform = '';
    });
  };

  // Global drag over handler for desktop drag preview
  useEffect(() => {
    const handleDragOver = (e: DragEvent) => {
      if (desktopDragPreview && draggedTile) {
        e.preventDefault(); // Important for drag to work
        // Update drag preview position during drag
        requestAnimationFrame(() => {
          setDesktopDragPreview(prev => prev ? {
            ...prev,
            x: e.clientX,
            y: e.clientY
          } : null);
        });
      }
    };

    if (desktopDragPreview) {
      document.addEventListener('dragover', handleDragOver);
    }

    return () => {
      document.removeEventListener('dragover', handleDragOver);
    };
  }, [desktopDragPreview, draggedTile]);

  // Drag over empty spot
  function handleDragOverTile(
    rowIndex: number,
    tileIndex: number,
    event: React.DragEvent<HTMLDivElement>
  ) {
    if (gameLost) return;
    event.preventDefault();
    setDragOver({ row: rowIndex, index: tileIndex });
  }
  function handleDragLeaveTile() {
    setDragOver(null);
  }

  // Responsive: show tiles beside rows on desktop, below on mobile
  return (
    <div className="board-padding-container" style={{ width: '100%' }}>
      <div className="board">
        {/* Top bar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            fontSize: 15,
            fontWeight: 500,
          }}
        >
          <span>{dateStr}</span>
          <div
            className="heart-guesses-desktop heart-guesses-container"
            style={{
              justifyContent: "center",
              gap: 7,
              margin: "24px 0 12px 0",
            }}
          >
            {[0, 1, 2, 3].map((i) => (
              <svg
                key={i}
                width={25}
                height={25}
                viewBox="0 0 25 25"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-label={incorrectGuesses > i ? "Lost life" : "Life remaining"}
              >
                <path
                  d="M23.4375 9.96094C23.4375 16.7969 13.3018 22.3301 12.8701 22.5586C12.7563 22.6198 12.6292 22.6518 12.5 22.6518C12.3708 22.6518 12.2436 22.6198 12.1299 22.5586C11.6982 22.3301 1.5625 16.7969 1.5625 9.96094C1.56431 8.35569 2.20279 6.81671 3.33788 5.68163C4.47296 4.54654 6.01194 3.90806 7.61719 3.90625C9.63379 3.90625 11.3994 4.77344 12.5 6.23926C13.6006 4.77344 15.3662 3.90625 17.3828 3.90625C18.9881 3.90806 20.527 4.54654 21.6621 5.68163C22.7972 6.81671 23.4357 8.35569 23.4375 9.96094Z"
                  fill={incorrectGuesses > i ? "#CFDFEE" : "#1C1C1E"}
                />
              </svg>
            ))}
          </div>
          <span style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <span
              style={{
                fontVariantNumeric: "tabular-nums",
                minWidth: 48,
                textAlign: "right",
              }}
            >
              {formatTime(seconds)}
            </span>
            <button
              aria-label="How to play"
              onClick={() => setShowHowTo(true)}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                marginLeft: 8,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M11.25 14.0625C11.25 14.3097 11.1767 14.5514 11.0393 14.757C10.902 14.9625 10.7068 15.1227 10.4784 15.2174C10.25 15.312 9.99862 15.3367 9.75614 15.2885C9.51366 15.2403 9.29094 15.1212 9.11612 14.9464C8.9413 14.7716 8.82225 14.5488 8.77402 14.3064C8.72579 14.0639 8.75054 13.8126 8.84515 13.5841C8.93976 13.3557 9.09998 13.1605 9.30554 13.0232C9.5111 12.8858 9.75278 12.8125 10 12.8125C10.3315 12.8125 10.6495 12.9442 10.8839 13.1786C11.1183 13.413 11.25 13.731 11.25 14.0625ZM18.4375 10C18.4375 11.6688 17.9427 13.3001 17.0155 14.6876C16.0884 16.0752 14.7706 17.1566 13.2289 17.7952C11.6871 18.4338 9.99064 18.6009 8.35393 18.2754C6.71721 17.9498 5.2138 17.1462 4.03379 15.9662C2.85379 14.7862 2.05019 13.2828 1.72463 11.6461C1.39907 10.0094 1.56616 8.31286 2.20477 6.77111C2.84338 5.22936 3.92484 3.9116 5.31238 2.98448C6.69992 2.05735 8.33122 1.5625 10 1.5625C12.237 1.56498 14.3817 2.45473 15.9635 4.03653C17.5453 5.61833 18.435 7.763 18.4375 10ZM16.5625 10C16.5625 8.70206 16.1776 7.43327 15.4565 6.35407C14.7354 5.27487 13.7105 4.43374 12.5114 3.93704C11.3122 3.44034 9.99272 3.31038 8.71972 3.5636C7.44672 3.81681 6.2774 4.44183 5.35962 5.35961C4.44183 6.27739 3.81682 7.44672 3.5636 8.71972C3.31038 9.99272 3.44034 11.3122 3.93704 12.5114C4.43374 13.7105 5.27488 14.7354 6.35407 15.4565C7.43327 16.1776 8.70206 16.5625 10 16.5625C11.7399 16.5606 13.408 15.8686 14.6383 14.6383C15.8686 13.408 16.5606 11.7399 16.5625 10ZM10 5C8.10469 5 6.5625 6.40156 6.5625 8.125V8.4375C6.5625 8.68614 6.66128 8.9246 6.83709 9.10041C7.01291 9.27623 7.25136 9.375 7.5 9.375C7.74864 9.375 7.9871 9.27623 8.16292 9.10041C8.33873 8.9246 8.4375 8.68614 8.4375 8.4375V8.125C8.4375 7.43594 9.14063 6.875 10 6.875C10.8594 6.875 11.5625 7.43594 11.5625 8.125C11.5625 8.81406 10.8594 9.375 10 9.375C9.75136 9.375 9.51291 9.47377 9.33709 9.64959C9.16128 9.8254 9.0625 10.0639 9.0625 10.3125V10.9375C9.06194 11.1691 9.14715 11.3927 9.30169 11.5653C9.45623 11.7378 9.66916 11.847 9.89945 11.8719C10.1297 11.8967 10.3611 11.8354 10.5488 11.6998C10.7366 11.5642 10.8676 11.3639 10.9164 11.1375C12.368 10.7719 13.4375 9.56016 13.4375 8.125C13.4375 6.40156 11.8953 5 10 5Z"
                  fill="#343330"
                />
              </svg>
            </button>
          </span>
        </div>
        <HowToPlayModal open={showHowTo} onClose={() => setShowHowTo(false)} />
        <Toast message={toast} onDone={clearToast} />
        <div
          className="board-rows-tiles-wrapper"
          style={{
            display: "flex",
            flexDirection: "row-reverse",
            margin: "0 auto",
            alignItems: "flex-start",
            gap: 32,
          }}
        >
          <div
            ref={rowsContainerRef}
            className="rows-container"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              flex: 1,
            }}
          >
            {rows.map((row, rowIndex) => (
              <div
                className={`row${completedRows[rowIndex] || gameLost ? " locked" : ""}${
                  lastIncorrectRows[rowIndex] ? " incorrect" : ""
                }`}
                key={rowIndex}
                style={{
                  background: completedRows[rowIndex] || gameLost
                    ? ["#9B5DE5", "#3A86FF", "#00BB7E", "#FFBE0B"][rowIndex]
                    : undefined,
                  border: lastIncorrectRows[rowIndex] && !gameLost
                    ? "1px solid #F0544F"
                    : completedRows[rowIndex] || gameLost
                    ? `1px solid ${
                        ["#9B5DE5", "#3A86FF", "#00BB7E", "#FFBE0B"][rowIndex]
                      }`
                    : "1px solid #DFE4E9",
                }}
                onDragOver={(e) => {
                  if (!completedRows[rowIndex] && !gameLost) e.preventDefault();
                }}
                onDrop={(e) => {
                  if (!completedRows[rowIndex] && !gameLost && draggedTile) {
                    // Dropping on row itself - always append to end
                    handleTileDropOnRow(rowIndex, -1); // -1 means append to end
                  }
                }}
                onClick={() => handleRowTap(rowIndex)}
              >
                {completedRows[rowIndex] || gameLost ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      flex: 1,
                      minHeight: 0,
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: 20,
                        color: "#fff",
                        fontFamily: "inherit",
                        marginRight: 10,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {(() => {
                        const g = row[0] ? iconGroups[row[0].icon] : undefined;
                        return (g !== undefined && groupNameMap[g]) ||
                          `Group ${(g ?? rowIndex) + 1}`;
                      })()}
                    </span>
                    <div
                      style={{
                        display: "flex",
                        gap: 10,
                        flex: 1,
                        alignItems: "center",
                      }}
                    >
                      {row.map(
                        (tile, tileIndex) =>
                          tile && (
                            <span
                              key={tileIndex}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "17.5px 0",
                                flex: 1,
                              }}
                            >
                              <img
                                src={"/icons/" + tile.icon}
                                alt={iconLabel(tile.icon)}
                                style={{
                                  width: 20,
                                  height: 20,
                                  filter: "brightness(0) invert(1)",
                                }}
                              />
                            </span>
                          )
                      )}
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      gap: 15,
                      flex: 1,
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {row.map((tile, tileIndex) => {
                      // Always color tiles by row
                      let tileBg = [
                        "#9B5DE5", // 1st row
                        "#3A86FF", // 2nd row
                        "#00BB7E", // 3rd row
                        "#FFBE0B", // 4th row
                      ][rowIndex];
                      return (
                        <div
                          key={tileIndex}
                          className={`tile${
                            dragOver &&
                            dragOver.row === rowIndex &&
                            dragOver.index === tileIndex
                              ? " drag-over"
                              : ""
                          }${completedRows[rowIndex] ? " locked" : ""}${isSelected(tile) ? " selected" : ""}${!tile && selected ? " tap-target" : ""}`}
                          role="button"
                          tabIndex={canAct ? 0 : -1}
                          aria-pressed={tile ? isSelected(tile) : undefined}
                          aria-label={tile ? `${iconLabel(tile.icon)}, row ${rowIndex + 1}` : `Empty spot, row ${rowIndex + 1}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (tile) handleTileTap(tile, { row: rowIndex, idx: tileIndex });
                            else handleRowTap(rowIndex);
                          }}
                          onKeyDown={tapKeys(() =>
                            tile ? handleTileTap(tile, { row: rowIndex, idx: tileIndex }) : handleRowTap(rowIndex)
                          )}
                          draggable={!!tile && !completedRows[rowIndex]}
                          onDragStart={
                            tile
                              ? (e) => handleTileDragStart(e, tile)
                              : undefined
                          }
                          onDragEnd={handleDesktopDragEnd}
                          onDragOver={(e) =>
                            handleDragOverTile(rowIndex, tileIndex, e)
                          }
                          onDrop={(e) => {
                            e.stopPropagation(); // Prevent row's onDrop from firing
                            handleTileDropOnRow(rowIndex, tileIndex);
                          }}
                          onDragLeave={handleDragLeaveTile}
                          style={tile ? { background: tileBg } : {}}
                          onTouchStart={
                            tile
                              ? (e) => handleTileTouchStart(tile, e)
                              : undefined
                          }
                          onTouchMove={handleTileTouchMove}
                          onTouchEnd={handleTileTouchEnd}
                        >
                          {tile ? (
                            <span
                              style={{
                                cursor: completedRows[rowIndex]
                                  ? "not-allowed"
                                  : "pointer",
                              }}
                            >
                              <img
                                src={"/icons/" + tile.icon}
                                alt={iconLabel(tile.icon)}
                                style={{ maxWidth: 48, maxHeight: 48 }}
                              />
                            </span>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
            <div
              className="heart-guesses-mobile heart-guesses-container"
              style={{
                justifyContent: "center",
                gap: 7,
                margin: "24px 0 12px 0",
              }}
            >
              {[0, 1, 2, 3].map((i) => (
                <svg
                  key={i}
                  width={25}
                  height={25}
                  viewBox="0 0 25 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-label={incorrectGuesses > i ? "Lost life" : "Life remaining"}
                >
                  <path
                    d="M23.4375 9.96094C23.4375 16.7969 13.3018 22.3301 12.8701 22.5586C12.7563 22.6198 12.6292 22.6518 12.5 22.6518C12.3708 22.6518 12.2436 22.6198 12.1299 22.5586C11.6982 22.3301 1.5625 16.7969 1.5625 9.96094C1.56431 8.35569 2.20279 6.81671 3.33788 5.68163C4.47296 4.54654 6.01194 3.90806 7.61719 3.90625C9.63379 3.90625 11.3994 4.77344 12.5 6.23926C13.6006 4.77344 15.3662 3.90625 17.3828 3.90625C18.9881 3.90806 20.527 4.54654 21.6621 5.68163C22.7972 6.81671 23.4357 8.35569 23.4375 9.96094Z"
                    fill={incorrectGuesses > i ? "#CFDFEE" : "#1C1C1E"}
                  />
                </svg>
              ))}
            </div>
          </div>
          <div
            ref={tilesContainerRef}
            className="tiles-container"
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(null);
            }}
            onDrop={handleDropToPool}
            onClick={handlePoolTap}
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gridTemplateRows: "repeat(4, 1fr)",
              marginTop: 0,
              justifyItems: "center",
              minWidth: 180,
              maxWidth: 392,
              width: "100%",
            }}
          >
            {poolTiles.length === 0 ? (
              Array.from({ length: 16 }, (_, index) => (
                <div
                  key={`placeholder-${index}`}
                  className="tile tile-invisible"
                  style={{
                    pointerEvents: "none",
                    opacity: 0,
                    minWidth: 79,
                    maxWidth: 79,
                    width: 79,
                    minHeight: 55,
                    maxHeight: 55,
                    height: 55,
                  }}
                  aria-hidden="true"
                />
              ))
            ) : (
              poolTiles.map((tile, index) => (
                <div
                  key={tile.icon}
                  className={`tile${isSelected(tile) ? " selected" : ""}`}
                  role="button"
                  tabIndex={canAct ? 0 : -1}
                  aria-pressed={isSelected(tile)}
                  aria-label={`${iconLabel(tile.icon)}, not placed`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTileTap(tile, "pool");
                  }}
                  onKeyDown={tapKeys(() => handleTileTap(tile, "pool"))}
                  draggable
                  onDragStart={(e) => handleTileDragStart(e, tile)}
                  onDragEnd={handleDesktopDragEnd}
                  style={{ background: "#CFDFEE" }}
                  onTouchStart={(e) => handleTileTouchStart(tile, e)}
                  onTouchMove={handleTileTouchMove}
                  onTouchEnd={handleTileTouchEnd}
                >
                  <img
                    src={"/icons/" + tile.icon}
                    alt={iconLabel(tile.icon)}
                    style={{ maxWidth: 48, maxHeight: 48 }}
                  />
                </div>
              ))
            )}
          </div>
        </div>
        {/* While tiles remain unplaced: Shuffle. Once all are placed: Submit / View Results */}
        {!gameLost && !completedRows.every(Boolean) && poolTiles.length > 0 ? (
          <button
            type="button"
            className="button-main white-invert"
            style={{ marginTop: 24 }}
            onClick={handleShuffle}
          >
            Shuffle
          </button>
        ) : (
          <button
            style={{
              marginTop: 24,
              borderRadius: "200px",
              background: "var(--Black, #1C1C1E)",
              padding: "12px 36px",
              color: "#fff",
              fontSize: 18,
              fontWeight: 600,
              border: "none",
              cursor: (poolTiles.length === 0 && !completedRows.every(Boolean)) || completedRows.every(Boolean) || gameLost ? "pointer" : "default",
              letterSpacing: 0.5,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              opacity: (poolTiles.length === 0 && !completedRows.every(Boolean)) || completedRows.every(Boolean) || gameLost ? 1 : 0,
              pointerEvents: (poolTiles.length === 0 && !completedRows.every(Boolean)) || completedRows.every(Boolean) || gameLost ? "auto" : "none",
            }}
            onClick={completedRows.every(Boolean) || gameLost ? () => setShowResults(true) : handleSubmit}
          >
            {completedRows.every(Boolean) || gameLost ? "View Results" : "Submit"}
          </button>
        )}
        {showResults && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0,0,0,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000,
            }}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: 12,
                padding: 32,
                maxWidth: 420,
                boxShadow: "0 2px 16px rgba(0,0,0,0.15)",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: 3,
                    background: "#fff",
                    margin: "16px 0",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    transition: "background 0.3s",
                  }}
                >
                  {[
                    { icon: "cookie-fill.svg", bg: "#9B5DE5" },
                    { icon: "ghost-fill.svg", bg: "#3A86FF" },
                    { icon: "club-fill.svg", bg: "#00BB7E" },
                    { icon: "finn-the-human-fill.svg", bg: "#FFBE0B" },
                  ].map((item, i) => (
                    <span
                      key={i}
                      style={{
                        width: "100%",
                        height: 25,
                        borderRadius: 200,
                        background: item.bg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxSizing: "border-box",
                        userSelect: "none",
                        pointerEvents: "none",
                        transition:
                          "background 0.3s, width 0.5s cubic-bezier(.7,0,.3,1), height 0.5s cubic-bezier(.7,0,.3,1)",
                      }}
                    >
                      <img
                        src={`/icons/${item.icon}`}
                        alt={item.icon.replace("-fill.svg", "")}
                        style={{
                          width: 20,
                          height: 20,
                          filter: "brightness(0) invert(1)",
                          transition:
                            "width 0.5s cubic-bezier(.7,0,.3,1), height 0.5s cubic-bezier(.7,0,.3,1), filter 0.3s",
                        }}
                        draggable={false}
                      />
                    </span>
                  ))}
                </div>

                <button
                  onClick={() => {
                    setShowResults(false);
                  }}
                  aria-label="Close"
                  style={{
                    boxShadow: "none",
                    background: "none",
                    border: "none",
                    padding: 0,
                    marginLeft: 16,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M6.4 19L5 17.6L10.6 12L5 6.4L6.4 5L12 10.6L17.6 5L19 6.4L13.4 12L19 17.6L17.6 19L12 13.4L6.4 19Z"
                      fill="#1C1C1E"
                    />
                  </svg>
                </button>
              </div>
              <div style={{ paddingTop: "80px", paddingBottom: "40px" }}>
                <h2
                  style={{
                    marginBottom: 0,
                    fontSize: 45,
                    fontFamily: "Monigue, Arial, sans-serif",
                    fontWeight: 400,
                  }}
                >
                  {completedRows.every(Boolean) ? "I solved this week's Matchset!" : "Thanks for playing Matchset!"}
                </h2>
                {isPreview ? <p className="countdown">Test run: nothing is saved or shared.</p> : <Countdown target={nextStartDate} />}
                
              </div>
              <div className="share-date">{dateStr}</div>
              <div className="share-grid" aria-label="Your guesses">
                {guessLog.map((g, i) => (
                  <div key={i}>{guessLine(g)}</div>
                ))}
              </div>
              <p className="share-summary">
                {summaryLine(completedRows.every(Boolean), guessLog.length, seconds)}
              </p>
              {!isPreview && <div className="share-actions">
                <button type="button" className="button-main" onClick={handleShare}>
                  <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
                    <img
                      src={"/icons/share-network-fill.svg"}
                      alt=""
                      style={{ width: 22, height: 22, filter: "brightness(0) invert(1)" }}
                      aria-hidden="true"
                    />
                    {copied ? "Copied!" : "Share Results"}
                  </span>
                </button>
              </div>}
              <br />
            </div>
          </div>
        )}
        {/* Auto Solve button (dev only) */}
        {isLocal && !isPreview && !completedRows.every(Boolean) && (
          <button
            style={{
              position: "absolute",
              top: 10,
              right: 10,
              borderRadius: "200px",
              background: "#888",
              padding: "8px 24px",
              color: "#fff",
              fontSize: 15,
              fontWeight: 500,
              border: "none",
              cursor: "pointer",
              letterSpacing: 0.5,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              zIndex: 1000,
            }}
            onClick={handleAutoSolve}
          >
            Auto Solve (dev only)
          </button>
        )}
        {/* Drag Preview for mobile and desktop */}
        {(dragPreview || desktopDragPreview) && (
          <div
            style={{
              position: "fixed",
              left: (dragPreview?.x || desktopDragPreview?.x || 0) - 40,
              top: (dragPreview?.y || desktopDragPreview?.y || 0) - 27.5,
              width: 80,
              height: 55,
              background: "#CFDFEE",
              borderRadius: "200px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              zIndex: 10000,
              boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
              transform: "scale(1.2)",
              opacity: 1,
              border: "3px solid #CFDFEE",
              WebkitTransform: "scale(1.2)",
              MozTransform: "scale(1.2)",
              WebkitBackfaceVisibility: "hidden",
              backfaceVisibility: "hidden",
              willChange: "transform",
              transition: "none",
            }}
          >
            <img
              src={"/icons/" + ((dragPreview || desktopDragPreview)?.tile.icon)}
              alt=""
              style={{ 
                width: 32,
                height: 32,
                filter: "brightness(0) invert(1)",
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Board;
